import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { gbp, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const empty = { driver_id: "", van_reg: "", date: todayISO(), time: "", location: "", reference: "", type: "Parking (PCN)", amount: "", notes: "", status: "open", deduct_from_earnings: false, evidence_file_id: "" };

export default function Tickets() {
  const { data: tickets, loading, refetch } = useFetch("/tickets");
  const { data: drivers } = useFetch("/drivers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t, amount: String(t.amount) }); setEditId(t.id); setOpen(true); };

  const uploadEvidence = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, evidence_file_id: data.file_id }));
      toast.success("Evidence uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0) };
      if (editId) await api.put(`/tickets/${editId}`, payload);
      else await api.post("/tickets", payload);
      toast.success(editId ? "Ticket updated" : "Ticket created");
      setOpen(false); refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this ticket?")) return;
    try { await api.delete(`/tickets/${id}`); toast.success("Deleted"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleDeduct = async (t) => {
    try { await api.put(`/tickets/${t.id}`, { deduct_from_earnings: !t.deduct_from_earnings }); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <Loader />;

  return (
    <div className="fp-fade-in">
      <PageHeader title="Tickets" subtitle={`${tickets?.length || 0} fines & penalties`} testId="tickets-page">
        <Button onClick={openNew} className="w-full sm:w-auto" data-testid="add-ticket-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add Ticket</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Driver</TableHead><TableHead>Van</TableHead>
                <TableHead>Type</TableHead><TableHead>Ref</TableHead><TableHead>Amount</TableHead>
                <TableHead>Deduct</TableHead><TableHead>Status</TableHead><TableHead>Evidence</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tickets || []).map((t) => (
                <TableRow key={t.id} data-testid={`ticket-row-${t.id}`}>
                  <TableCell className="font-mono text-sm">{ukDate(t.date)}</TableCell>
                  <TableCell className="font-medium">{t.driver_name}</TableCell>
                  <TableCell>{t.van_reg ? <Plate reg={t.van_reg} /> : "—"}</TableCell>
                  <TableCell>{t.type}</TableCell>
                  <TableCell className="font-mono text-xs">{t.reference || "—"}</TableCell>
                  <TableCell className="font-mono font-semibold text-rose-400">{gbp(t.amount)}</TableCell>
                  <TableCell><Switch checked={t.deduct_from_earnings} onCheckedChange={() => toggleDeduct(t)} data-testid={`deduct-switch-${t.id}`} /></TableCell>
                  <TableCell><StatusPill status={t.status} /></TableCell>
                  <TableCell>
                    {t.evidence_file_id ? (
                      <a href={`${API}/files/${t.evidence_file_id}`} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">View</a>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)}><Icons.Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(t.id)}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(!tickets || tickets.length === 0) && <EmptyState icon="Receipt" title="No tickets" subtitle="Use the Ticket Finder to raise one." />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Ticket" : "New Ticket"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Driver</Label>
              <Select value={form.driver_id || "none"} onValueChange={(v) => set("driver_id")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-driver-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Van</Label><Input value={form.van_reg} onChange={set("van_reg")} className="mt-1.5 font-mono" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" /></div>
            <div><Label>Time</Label><Input type="time" value={form.time} onChange={set("time")} className="mt-1.5" /></div>
            <div><Label>Type</Label><Input value={form.type} onChange={set("type")} className="mt-1.5" data-testid="ticket-type-input" /></div>
            <div><Label>Amount (£)</Label><Input type="number" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="ticket-amount-input" /></div>
            <div><Label>Reference</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={set("location")} className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Evidence Photo</Label>
              <Input type="file" accept="image/*" onChange={(e) => uploadEvidence(e.target.files[0])} className="mt-1.5" data-testid="ticket-evidence-input" />
              {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
              {form.evidence_file_id && <p className="text-xs text-emerald-400 mt-1">Attached ✓</p>}
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={set("notes")} className="mt-1.5" /></div>
            <div className="sm:col-span-2 flex items-center gap-3 rounded-lg bg-muted p-3">
              <Switch checked={form.deduct_from_earnings} onCheckedChange={set("deduct_from_earnings")} data-testid="ticket-deduct-switch" />
              <div><p className="text-sm font-medium">Deduct from driver earnings</p><p className="text-xs text-muted-foreground">Reduces the driver's net income by this amount.</p></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-ticket-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
